import { useState } from 'react';
import styled from 'styled-components';
import { ModalWidthEnum } from 'components/Base/Modal';
import AdaptiveModal from 'components/Base/Modal/AdaptiveModal/AdaptiveModal';
import { Text } from 'components/Base/Typography';
import { TAnimalTypeRetractionNoticeUI } from 'data/types/animal.types';
import AnimalTypeNotManagedAlert from 'components/Shared/AnimalTypeNotManagedAlert';

/**
 * PP-10057. Tells the tenant that the action they just took left an animal's application retracted,
 * because OurPetPolicy does not manage that animal type at the property.
 *
 * One dialog per notice, shown in sequence: a single action can affect several residencies at once,
 * since an animal auto-applies to every active residency, and each property configures its own
 * wording. The block is the same one the onboarding cards use, so the explanation reads identically
 * wherever the resident meets it.
 */

type TProps = {
  notices: TAnimalTypeRetractionNoticeUI[];
  onClose: () => void;
};

const AnimalTypeRetractionNoticeModal = ({ notices, onClose }: TProps) => {
  const [index, setIndex] = useState(0);
  const notice = notices[index];

  if (!notice) {
    return null;
  }

  const isLast = index === notices.length - 1;

  return (
    <SNoticeModal
      title="Animal is Retracted"
      size={ModalWidthEnum.Small}
      visible
      destroyOnClose
      // The warning block carries its own generous padding, so the modal's default 24px below it
      // leaves the button floating away from the message it acknowledges.
      // The warning block no longer reserves 24px below itself, so the body carries the whole gap
      // to "Got it" — kept at the spacing that was signed off rather than collapsing to 8px.
      bodyStyle={{ paddingBottom: 32 }}
      okText={isLast ? 'Got it' : 'Next'}
      onOk={() => (isLast ? onClose() : setIndex(index + 1))}
      cancelButtonProps={{ style: { display: 'none' } }}
      onCancel={onClose}
    >
      <AnimalTypeNotManagedAlert
        animalType={notice.animalType}
        description={notice.message || ''}
      />
      {notices.length > 1 && (
        <div className="mt-3">
          <Text fontLevel={5}>{`${index + 1} of ${notices.length}`}</Text>
        </div>
      )}
    </SNoticeModal>
  );
};

/*
 * The dialog only acknowledges a message, so its single button reads as centred under it rather
 * than tucked into the corner a form's actions would occupy. Needs &&& to outrank the ui-kit's own
 * && -boosted `text-align: right` on the same element.
 */
const SNoticeModal = styled(AdaptiveModal)`
  &&& {
    .ant-modal-footer,
    .ant-drawer-footer {
      text-align: center;
    }
  }
`;

export default AnimalTypeRetractionNoticeModal;
